import { useState } from 'react';

import PropTypes from 'prop-types';

import { formatDateToYYYYMMDD } from '@shared/utils/formatDate';

import styles from './MobileDocumentInfoSection.module.scss';

/**
 * 모바일 문서 정보 섹션 컴포넌트
 * - 기안 정보 및 참조/시행 섹션
 * - 접기/펼치기 기능
 * - 접힌 상태: 기안일자, 기안자
 * - 펼친 상태: 보존년한, 문서번호, 기안일자, 기안부서, 기안자, 참조자, 시행자
 */
function MobileDocumentInfoSection({
  documentInfo,
  docData,
  receivers,
  executors,
  onReceiverClick,
  onExecutorClick,
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // 날짜 포맷팅 (YYYY-MM-DD 형식)
  const formattedDate = formatDateToYYYYMMDD(docData?.date);

  // 보존년한
  const retentionPeriod = docData?.keep_year ? `${docData.keep_year}년` : '-';

  // 참조자 표시 텍스트
  const receiverText = getPersonListText(receivers);
  const hasMultipleReceivers = receivers && receivers.length > 1;

  // 시행자 표시 텍스트
  const executorText = getPersonListText(executors);
  const hasMultipleExecutors = executors && executors.length > 1;

  function toggleExpand() {
    setIsExpanded(!isExpanded);
  }

  function handleReceiverClick() {
    if (hasMultipleReceivers && onReceiverClick) {
      onReceiverClick(receivers);
    }
  }

  function handleExecutorClick() {
    if (hasMultipleExecutors && onExecutorClick) {
      onExecutorClick(executors);
    }
  }

  return (
    <div className={styles.container}>
      {/* 섹션 헤더 */}
      <div className={styles.sectionHeader}>
        <button type="button" className={styles.titleButton} onClick={toggleExpand}>
          <span className={styles.sectionTitle}>기안 정보 및 참조/시행</span>
          <svg
            className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
          >
            <path
              d="M6 7.5L9 10.5L12 7.5"
              stroke="#828282"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* 테이블 형태의 정보 */}
      <div className={styles.infoTable}>
        {/* 접힌 상태에서도 보이는 항목 */}
        <div className={styles.topBar} />

        <div className={styles.row}>
          <div className={styles.labelCell}>기안일자</div>
          <div className={styles.valueCell}>{formattedDate}</div>
        </div>

        <div className={styles.divider} />

        <div className={styles.row}>
          <div className={styles.labelCell}>기안자</div>
          <div className={styles.valueCell}>{documentInfo?.drafter || docData?.name || '-'}</div>
        </div>

        <div className={styles.divider} />

        {/* 펼친 상태에서 추가로 보이는 항목 */}
        {isExpanded && (
          <>
            <div className={styles.row}>
              <div className={styles.labelCell}>보존년한</div>
              <div className={styles.valueCell}>{retentionPeriod}</div>
            </div>

            <div className={styles.divider} />

            <div className={styles.row}>
              <div className={styles.labelCell}>문서번호</div>
              <div className={styles.valueCell}>{docData?.doc_number || '-'}</div>
            </div>

            <div className={styles.divider} />

            <div className={styles.row}>
              <div className={styles.labelCell}>기안부서</div>
              <div className={styles.valueCell}>
                {documentInfo?.draftingDepartment || docData?.team || '-'}
              </div>
            </div>

            <div className={styles.divider} />

            {/* 참조자 */}
            <div className={styles.row}>
              <div className={styles.labelCell}>참조자</div>
              <div className={styles.valueCell}>
                {hasMultipleReceivers ? (
                  <button
                    type="button"
                    className={styles.clickableValue}
                    onClick={handleReceiverClick}
                  >
                    {receiverText}
                  </button>
                ) : (
                  receiverText
                )}
              </div>
            </div>

            <div className={styles.divider} />

            {/* 시행자 */}
            <div className={styles.row}>
              <div className={styles.labelCell}>시행자</div>
              <div className={styles.valueCell}>
                {hasMultipleExecutors ? (
                  <button
                    type="button"
                    className={styles.clickableValue}
                    onClick={handleExecutorClick}
                  >
                    {executorText}
                  </button>
                ) : (
                  executorText
                )}
              </div>
            </div>

            <div className={styles.divider} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 인원 목록 텍스트 생성
 * - 1명: 이름만 표시
 * - 2명 이상: "이름 외 N명" 형식
 */
function getPersonListText(persons) {
  if (!persons || persons.length === 0) {
    return '-';
  }

  const firstName = persons[0].name || '-';

  if (persons.length === 1) {
    return firstName;
  }

  return `${firstName} 외 ${persons.length - 1}명`;
}

MobileDocumentInfoSection.propTypes = {
  documentInfo: PropTypes.shape({
    drafter: PropTypes.string,
    draftingDepartment: PropTypes.string,
  }),
  docData: PropTypes.shape({
    name: PropTypes.string,
    team: PropTypes.string,
    date: PropTypes.string,
    doc_number: PropTypes.string,
    keep_year: PropTypes.number,
  }),
  receivers: PropTypes.arrayOf(
    PropTypes.shape({
      enumber: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  executors: PropTypes.arrayOf(
    PropTypes.shape({
      enumber: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  onReceiverClick: PropTypes.func,
  onExecutorClick: PropTypes.func,
};

MobileDocumentInfoSection.defaultProps = {
  documentInfo: {},
  docData: {},
  receivers: [],
  executors: [],
  onReceiverClick: null,
  onExecutorClick: null,
};

export default MobileDocumentInfoSection;
